import React, { useEffect, useMemo, useRef, useState } from "react";

import { Panel } from "primereact/panel";
import { ProgressBar } from "primereact/progressbar";

import { categorizeScores } from "../../../helpers/scores";
import { sanitizeTestId } from "../../../utils/string";

import {
  SummaryTopicCardProps,
  SummaryTopicCardHeaderTemplateProps,
  TopicScore,
} from "./summaryTopicCardTypes";

import S from "./SummaryTopicCard.module.scss";

export const SummaryTopicCard: React.FC<SummaryTopicCardProps> = ({
  categoryScore,
  topicScoresAverage,
  expanded,
}) => {
  const ref = useRef<Panel>(null);
  const categoryTopicScores = useMemo(
    () => topicScoresAverage[categoryScore.category] || [],
    [topicScoresAverage, categoryScore.category]
  );
  const { poorTopics, strongTopics, fairTopics } = useMemo(
    () => categorizeScores(categoryTopicScores),
    [categoryTopicScores]
  );
  const [topicScore, setTopicScore] = useState<number | null>(null);

  useEffect(() => {
    if (ref.current) {
      if (expanded) {
        ref.current.expand(undefined);
      } else {
        ref.current.collapse(undefined);
      }
    }
  }, [expanded]);

  useEffect(() => {
    setTimeout(() => {
      setTopicScore(categoryScore.averageScore);
    }, 500);
  }, [categoryScore]);

  return (
    <Panel
      ref={ref}
      toggleable
      collapsed={true}
      expandIcon={<i className="pi pi-angle-left"></i>}
      collapseIcon={<i className="pi pi-angle-down"></i>}
      header={categoryScore.category}
      data-testid={`summary-topic-card-${sanitizeTestId(categoryScore.category)}`}
      headerTemplate={props => (
        <SummaryTopicCardHeaderTemplate
          {...props}
          topicScore={topicScore}
          category={categoryScore.category}
        />
      )}
      pt={{
        content: { className: S.categoryContent },
      }}
    >
      {categoryTopicScores.length > 0 && (
        <div className={S.topicWrapper}>
          {strongTopics.length > 0 && (
            <div className={S.topicContent}>
              <div className="body-sm text-surface-400">Strengths</div>
              {strongTopics.map((topicScore: TopicScore) => (
                <div key={topicScore.topic} className={S.topicItem}>
                  <i className="pi pi-check"></i>
                  {topicScore.topic}
                </div>
              ))}
            </div>
          )}
          {fairTopics.length > 0 && (
            <div className={S.topicContent}>
              <div className="body-sm text-surface-400">Proficiencies</div>
              {fairTopics.map((topicScore: TopicScore) => (
                <div key={topicScore.topic} className={S.topicItem}>
                  <i className="pi pi-minus"></i> {topicScore.topic}
                </div>
              ))}
            </div>
          )}
          {poorTopics.length > 0 && (
            <div className={S.topicContent}>
              <div className="body-sm text-surface-400">Growth Opportunities</div>
              {poorTopics.map((topicScore: TopicScore) => (
                <div key={topicScore.topic} className={S.topicItem}>
                  <i className="pi pi-exclamation-triangle"></i> {topicScore.topic}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </Panel>
  );
};

const SummaryTopicCardHeaderTemplate: React.FC<SummaryTopicCardHeaderTemplateProps> = ({
  onTogglerClick,
  titleElement,
  togglerClassName,
  togglerElement,
  topicScore,
  category,
}) => {
  const score = topicScore || 0;
  return (
    <div className={S.categoryHeader} onClick={onTogglerClick}>
      <div className={S.categoryTitle}>
        <h2 className="atlas-h5">{titleElement}</h2>
        <div className={togglerClassName}>{togglerElement}</div>
      </div>
      <ProgressBar
        value={score}
        color="var(--indigo-dataviz-2)"
        aria-label={`${category} score: ${score}%`}
        pt={{ label: { className: "text-surface-0" }, root: { className: S.progressContainer } }}
      ></ProgressBar>
    </div>
  );
};
